import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { FastTrackRouteQueryParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    forLifeOf,
    LifecycleAware,
    focusOnFirstInvalidLsqInput,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { LoginPageStep, LoginService } from '../login.service';

@LifecycleAware()
@Component({
    selector: 'sign-in',
    styleUrls: ['./login-step.scss'],
    template: `
        <h1 i18n>Sign In</h1>

        <form class="login-step" [formGroup]="formGroup" (ngSubmit)="signIn()">
            <p class="error" *ngIf="showLoginError" i18n>
                Error: Invalid email address/password. Please try again.
            </p>
            <p class="error" *ngIf="!!showPasswordReset" i18n>
                {{ this.passwordResetCopy }}
            </p>
            <p
                *ngIf="showEnrolledCopy"
                class="body redirect-message"
                [style.textAlign]="'center'"
                [style.marginBottom]="'2rem'"
            >
                <span i18n
                    >Your company is already enrolled in FastTrack! Please log into your account or
                    contact LSQ support for further assistance at</span
                ><br />
                <a href="mailto:fasttrack-support@lsq.com" class="preserve-case">
                    fasttrack-support@lsq.com
                </a>
            </p>
            <p
                *ngIf="showNetworkRequestCopy"
                class="body redirect-message"
                [style.textAlign]="'center'"
                [style.marginBottom]="'2rem'"
                i18n
            >
                You have a new network request! Please sign in to your account to view and manage
                the request.
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'">Email Address</p>
            <lsq-input
                [style.marginBottom]="'2rem'"
                name="emailAddress"
                type="email"
                [basicLabel]="false"
                formControlName="emailAddress"
                [group]="formGroup"
            >
            </lsq-input>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'">Password</p>
            <lsq-input
                [style.marginBottom]="'3rem'"
                type="password"
                [basicLabel]="false"
                name="password"
                formControlName="password"
                [group]="formGroup"
            >
            </lsq-input>

            <lsq-button
                type="submit"
                size="block"
                [submitting]="isSubmitting"
                [style.marginBottom]="'2rem'"
            >
                Sign In
            </lsq-button>

            <lsq-button appearance="transparent" size="block" (buttonClick)="showForgotPassword()">
                Forgot your password?
            </lsq-button>
        </form>
    `
})
export class SignInComponent implements OnInit, OnDestroy {
    public isSubmitting = false;
    public showLoginError = false;
    public showEnrolledCopy = false;
    public showNetworkRequestCopy = false;
    @Input() public showPasswordReset? = false;
    public passwordResetCopy =
        'In order to better protect your data, we have upgraded our security infrastructure requiring a password reset.  A password reset link has been sent to your email from noreply@lsq.com.  Please use this link to access LSQ FastTrack Dashboard.';

    public formGroup = this._formBuilder.group({
        emailAddress: ['', [Validators.required, LsqValidators.email]],
        password: ['', [Validators.required]]
    });

    constructor(private _formBuilder: FormBuilder, private _loginService: LoginService) {}

    public ngOnInit(): void {
        this._loginService.routeParams$.pipe(forLifeOf(this)).subscribe((params) => {
            if (params.get(FastTrackRouteQueryParams.IsEnrolled)) {
                this.showEnrolledCopy = true;
            }
            if (params.get(FastTrackRouteQueryParams.HasNewNetworkRequest) === 'true') {
                this.showNetworkRequestCopy = true;
            }
        });
    }

    public ngOnDestroy(): void {}

    public async signIn(): Promise<void> {
        this.showPasswordReset = false;
        this.formGroup.markAllAsTouched();

        if (this.formGroup.valid) {
            this.isSubmitting = true;

            try {
                await this._loginService.loginWithEmailAndPassword(
                    this.formGroup.get('emailAddress').value,
                    this.formGroup.get('password').value
                );
            } catch (error) {
                if (error.type == 'Password reset required, please check your email.') {
                    this.showLoginError = false;
                    this.showPasswordReset = true;
                    this.isSubmitting = false;
                } else {
                    this.showPasswordReset = false;
                    this.showLoginError = true;
                    this.isSubmitting = false;
                }
            }
        } else {
            focusOnFirstInvalidLsqInput();
            this.isSubmitting = false;
        }
    }

    public showForgotPassword(): void {
        this._loginService.setLoginPageStep(LoginPageStep.ForgotPassword);
    }
}
